/**
 * Malaysian Ringgit (RM) 금액 → 영문 단어 변환 유틸 (재사용).
 *
 *   ringgitToWords(10000)      → "Ringgit Malaysia Ten Thousand Only"
 *   ringgitToWords(12345.67)   → "Ringgit Malaysia Twelve Thousand Three Hundred Forty Five and Sixty Seven Sen Only"
 *   ringgitToWords(10000.50)   → "Ringgit Malaysia Ten Thousand and Fifty Sen Only"
 *   ringgitToWords('')/invalid → ""  (빈 문자열 — 호출부에서 필드 clear 용도)
 *
 * 규칙: Prefix "Ringgit Malaysia", Suffix "Only", 소수부는 "Sen"(2자리). 정수부는 "and" 없이 연결.
 * RM 금액을 단어로 바꿔야 하는 다른 화면에서도 그대로 재사용할 것.
 */

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const SCALES = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];

/** 0..999 → 단어 */
function threeDigitToWords(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds > 0) parts.push(ONES[hundreds], 'Hundred');
  if (rest > 0) {
    if (rest < 20) {
      parts.push(ONES[rest]);
    } else {
      parts.push(TENS[Math.floor(rest / 10)]);
      if (rest % 10 > 0) parts.push(ONES[rest % 10]);
    }
  }
  return parts.join(' ');
}

/** 0 이상 정수 → 영문 단어 (미국식, "and" 미포함). 재사용 가능. */
export function integerToEnglishWords(value: number): string {
  const n = Math.floor(Math.abs(value));
  if (!Number.isFinite(n)) return '';
  if (n === 0) return 'Zero';

  const groups: number[] = [];
  let x = n;
  while (x > 0) {
    groups.push(x % 1000);
    x = Math.floor(x / 1000);
  }

  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    parts.push(threeDigitToWords(groups[i]));
    if (i > 0 && SCALES[i]) parts.push(SCALES[i]);
  }
  return parts.join(' ');
}

/**
 * RM 금액 → "Ringgit Malaysia ... Only" 문자열.
 * @param value 숫자 또는 문자열(콤마 허용). 유효하지 않거나 비어있으면 "" 반환.
 */
export function ringgitToWords(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim().replace(/,/g, '');
  if (raw === '') return '';

  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return '';

  // 2자리(Sen)로 반올림 후 정수(Ringgit)/소수(Sen) 분리 — 부동소수 오차 방지 위해 정수 연산.
  const totalSen = Math.round(num * 100);
  const ringgit = Math.floor(totalSen / 100);
  const sen = totalSen % 100;

  let result = `Ringgit Malaysia ${integerToEnglishWords(ringgit)}`;
  if (sen > 0) {
    result += ` and ${integerToEnglishWords(sen)} Sen`;
  }
  result += ' Only';
  return result;
}

export default ringgitToWords;
